/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Badge,
    Box,
    Button,
    Flex,
    Center,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {BasketIcon, BrandLogo} from '@salesforce/retail-react-app/app/components/icons'
import {HOME_HREF} from '@salesforce/retail-react-app/app/constants'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import sizes from '../../../theme/foundations/sizes'
import {pxToRem} from '../../../utills/pxToRem'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {backButton, cartImage, headerWrapper, logoImage} from './checkout-header.style'
import PropTypes from 'prop-types'

const CheckoutHeader = ({isCheckout, isPayPalPaymentPage = false}) => {
    const intl = useIntl()
    const {
        derivedData: {totalItems},
        data: basket
    } = useCurrentBasket()

    let giftCertsCount = 0

    if (basket?.giftCertificateItems) {
        giftCertsCount = basket.giftCertificateItems.length
    }
    return (
        <Box
            bg="rgba(0, 0, 0, 0.50)"
            backdrop-filter="blur(10px)"
            borderBottom="1px"
            borderColor="gray.100"
        >
            <Box maxWidth={sizes.container.xxl} marginLeft="auto" marginRight="auto">
                <Flex
                    h={{base: pxToRem(60), md: pxToRem(80)}}
                    align="center"
                    justify="space-between"
                    sx={headerWrapper}
                >
                    <Link
                        href={isPayPalPaymentPage ? undefined : HOME_HREF}
                        pointerEvents={isPayPalPaymentPage ? 'none' : 'auto'}
                        cursor={isPayPalPaymentPage ? 'default' : 'pointer'}
                        title="Back to homepage"
                    >
                        <Image
                            sx={logoImage}
                            src={getAssetUrl('static/img/checkout/checkout_header.png')}
                            alt="Cavender's"
                        />
                    </Link>

                    {!isPayPalPaymentPage && (
                        <Button
                            as={Link}
                            href="/cart"
                            display="inline-flex"
                            variant="unstyled"
                            color="white"
                            rightIcon={
                                <Center
                                    position="relative"
                                    width={[pxToRem(27), pxToRem(27), pxToRem(41), pxToRem(41)]}
                                    height={[pxToRem(27), pxToRem(27), pxToRem(41), pxToRem(41)]}
                                    marginLeft={0}
                                    marginRight={pxToRem(10)}
                                >
                                    <Image
                                        sx={cartImage}
                                        src={getAssetUrl('static/img/checkout/cart_white.svg')}
                                        alt="Cavender's"
                                    />
                                    {isCheckout && (
                                        <Badge
                                            variant="notification"
                                            bg={'cav_red'}
                                            alignItems={'center'}
                                            right={[
                                                pxToRem(-14),
                                                pxToRem(-14),
                                                pxToRem(-12),
                                                pxToRem(-12)
                                            ]}
                                            top={[pxToRem(-5), pxToRem(-5), pxToRem(0), pxToRem(0)]}
                                        >
                                            {totalItems + giftCertsCount}
                                        </Badge>
                                    )}
                                </Center>
                            }
                            aria-label={intl.formatMessage(
                                {
                                    id: 'checkout_header.link.assistive_msg.cart',
                                    defaultMessage: 'Back to cart, number of items: {numItems}'
                                },
                                {numItems: totalItems}
                            )}
                            sx={backButton}
                        >
                            <FormattedMessage
                                defaultMessage="Back to cart"
                                id="checkout_header.link.cart"
                            />
                        </Button>
                    )}
                </Flex>
            </Box>
        </Box>
    )
}

CheckoutHeader.propTypes = {
    isCheckout: PropTypes.bool,
    isPayPalPaymentPage: PropTypes.bool
}

export default CheckoutHeader
